import React, { useState } from 'react';
import { FaCheckCircle, FaTimesCircle, FaQuestionCircle, FaSpinner, FaUsers } from 'react-icons/fa';
import RSVPDetailsModal from './RSVPDetailsModal';
import styles from './RSVPButtons.module.css';

const RSVPButtons = ({ 
  eventId, 
  event, 
  userRSVP, 
  summary, 
  onRSVPUpdate, 
  canRSVP, 
  submitting,
  error,
  onSubmit,
  onClearError 
}) => {
  const [showAttendees, setShowAttendees] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [showRSVPModal, setShowRSVPModal] = useState(false);

  // Handle RSVP button click
  const handleRSVP = async (response) => {
    if (submitting || !canRSVP) return;
    
    setSelectedResponse(response);
    const success = await onSubmit(response);
    
    if (success && onRSVPUpdate) {
      onRSVPUpdate();
    }
    
    setSelectedResponse(null);
  };

  // Get button style based on current RSVP and button type
  const getButtonStyle = (response) => {
    const isCurrentResponse = userRSVP?.response === response;
    const isSelected = selectedResponse === response;
    
    let baseClass = styles.rsvpButton;
    
    switch (response) {
      case 'Going':
        baseClass += ` ${styles.responseGoing}`;
        break;
      case 'Not Going':
        baseClass += ` ${styles.responseNotGoing}`;
        break;
      case 'TBD':
        baseClass += ` ${styles.responseTBD}`;
        break;
      default:
        break;
    }
    
    if (isCurrentResponse) {
      baseClass += ` ${styles.active}`;
    }
    
    if (isSelected && submitting) {
      baseClass += ` ${styles.loading}`;
    }
    
    return baseClass;
  };

  // Get icon for response type
  const getResponseIcon = (response) => {
    switch (response) {
      case 'Going':
        return <FaCheckCircle />;
      case 'Not Going':
        return <FaTimesCircle />;
      case 'TBD':
        return <FaQuestionCircle />;
      default:
        return null;
    }
  };

  // Show loading spinner for current action
  const showLoadingForButton = (response) => {
    return submitting && selectedResponse === response;
  };

  // Format RSVP deadline
  const formatDeadline = () => {
    if (!event?.rsvpDeadline) return null;
    
    const deadline = new Date(event.rsvpDeadline);
    const now = new Date();
    
    if (deadline < now) {
      return (
        <div className={styles.deadlineMessage}>
          <span className={styles.deadlineExpired}>RSVP deadline has passed</span>
        </div>
      );
    }
    
    const diffHours = Math.ceil((deadline - now) / (1000 * 60 * 60));
    
    if (diffHours <= 24) {
      return (
        <div className={styles.deadlineMessage}>
          <span className={styles.deadlineUrgent}>
            RSVP deadline: {diffHours}h remaining
          </span>
        </div>
      );
    }
    
    return (
      <div className={styles.deadlineMessage}>
        <span className={styles.deadlineNormal}>
          RSVP by: {deadline.toLocaleDateString()}
        </span>
      </div>
    );
  };

  // Can't RSVP message
  if (!canRSVP) {
    return (
      <div className={styles.rsvpContainer}>
        {formatDeadline()}
        <div className={styles.rsvpDisabled}>
          <span>RSVP not available</span>
        </div>
        {summary.total > 0 && (
          <div className={styles.rsvpRow}>
            <div className={styles.rsvpSummary}>
              <button
                className={styles.summaryButton}
                onClick={() => setShowRSVPModal(true)}
                title="View detailed RSVPs"
              >
                <FaUsers />
                <span>{summary.going} going, {summary.total} total</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.rsvpContainer}>
      {formatDeadline()}
      
      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage}>
          <span>{error}</span>
          <button onClick={onClearError} className={styles.clearError}>×</button>
        </div>
      )}

      {/* RSVP Buttons and Summary Row */}
      <div className={styles.rsvpRow}>
        <div className={styles.responseButtons}>
          {['Going', 'Not Going', 'TBD'].map((response) => (
            <button
              key={response}
              className={getButtonStyle(response)}
              onClick={() => handleRSVP(response)}
              disabled={submitting || !canRSVP}
              title={`RSVP: ${response}`}
            >
              {showLoadingForButton(response) ? (
                <FaSpinner className={styles.spinner} />
              ) : (
                getResponseIcon(response)
              )}
              <span>{response === 'Not Going' ? 'No' : response === 'TBD' ? 'Maybe' : response}</span>
            </button>
          ))}
        </div>

        {/* RSVP Summary */}
        {summary.total > 0 && (
          <div className={styles.rsvpSummary}>
            <button
              className={styles.summaryButton}
              onClick={() => setShowRSVPModal(true)}
              title="View detailed RSVPs"
            >
              <FaUsers />
              <span className={styles.summaryText}>
                <span className={styles.goingCount}>{summary.going}</span> going
                {summary.total > summary.going && (
                  <span className={styles.totalCount}>, {summary.total} total</span>
                )}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Current User RSVP Status */}
      {userRSVP && (
        <div className={styles.userRSVPStatus}>
          <span className={styles.statusText}>
            You RSVP'd: <strong>{userRSVP.response}</strong>
          </span>
          {userRSVP.note && (
            <span className={styles.userNote} title={userRSVP.note}>
              "{userRSVP.note}"
            </span>
          )}
        </div>
      )}

      {/* RSVP Details Modal */}
      <RSVPDetailsModal
        eventId={eventId}
        event={event}
        isOpen={showRSVPModal}
        onClose={() => setShowRSVPModal(false)}
        initialTab="all"
      />
    </div>
  );
};

export default RSVPButtons;
